import { Trans, t } from '@lingui/macro';
import { LogOut, Star, EyeOff, BellOff, PhoneOff, MailX, Bell, Trash2 } from 'lucide-react';
import { useEffect, useState } from "react";
import { useAppUIContext } from "../../context/AppUIContext";
import { useHistory } from "../../hooks/useRouterCompat";
import { Room } from "../../models/Room";
import { RoomSetting } from "../../models/User";
import { Workspace } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

import { cn } from "../../lib/utils";

interface IRoomOptionModal {
  room: Room;
  workspace: Workspace | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// 채널 나가기/삭제 후 이동 경로. 다른 워크스페이스로 튀지 않도록
// 해당 워크스페이스의 일반 채널(1:1/개인방 제외)로만 이동하고,
// 남은 채널이 없으면 워크스페이스 홈, 그것도 없으면 /welcome.
export function getExitNavigationPath(excludeRoomId: string | undefined, workspaceId: string | null | undefined): string {
  const otherRooms = Array.from(My.Rooms.values()).filter(r =>
    r.id != excludeRoomId
    && !r.isOneToOne
    && r.visibility !== 'personal'
    && r.workspaceId === workspaceId
  );
  if (otherRooms.length > 0) return '/channel/' + otherRooms[0].id;
  if (workspaceId) return '/ws/' + workspaceId;
  return '/welcome';
}

export const MyRoomOptionModal: React.FC<IRoomOptionModal> = ({ room, workspace, isOpen, onOpenChange }) => {
  const [roomCopy, setRoomCopy] = useState<Room | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();
  const { activeWorkspaceId } = useAppUIContext();
  const isCreator = roomCopy?.creatorId && roomCopy.creatorId == My.user?.id;
  const isLegacyPersonalRoom = roomCopy?.visibility === 'personal' && !roomCopy?.workspaceId;
  const history = useHistory();
  const roomName = roomCopy ? Room.getRoomNameWithSharp(roomCopy, My.user.id) : '';

  function onPresent() {
    setRoomCopy(Util.pojo(room));
  }

  async function leaveRoom() {
    onOpenChange(false);
    if (roomCopy) {
      setTimeout(async () => {
        Fire.leaveRoom(roomCopy.id, My.user);
        navigateSomewhereElse();
      }, 500);
      toast({
        title: t`You left ${roomCopy.name}`,
        duration: 2000,
      });
    }
  }

  async function deleteRoom() {
    onOpenChange(false);
    if (roomCopy) {
      setTimeout(async () => {
        await Fire.deleteRoom(roomCopy.id);
        navigateSomewhereElse();
        toast({
          title: t`Channel deleted`,
          duration: 2000,
        });
      }, 500);
    }
  }

  function navigateSomewhereElse() {
    const workspaceId = workspace?.id ?? roomCopy?.workspaceId ?? activeWorkspaceId;
    history.replace(getExitNavigationPath(roomCopy?.id, workspaceId));
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]" onOpenAutoFocus={onPresent}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {roomName && (
                <>
                  <span>{roomName}</span>
                  <span className="text-muted-foreground">·</span>
                </>
              )}
              <Trans>My Settings</Trans>
            </DialogTitle>
          </DialogHeader>

          {roomCopy && (
            <div className="space-y-4 py-4">
              <PersonalRoomSettings roomId={roomCopy.id} onSave={() => onOpenChange(false)} />

              {(!isCreator || roomCopy.isOneToOne) && (
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setShowLeaveDialog(true)}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <Trans>Leave this channel</Trans>
                  </Button>
                </div>
              )}

              {/* Delete button for legacy My Room (personal room without workspace) */}
              {isCreator && isLegacyPersonalRoom && (
                <div className={cn(!(!isCreator || roomCopy.isOneToOne) && "pt-4 border-t")}>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <Trans>Delete this channel</Trans>
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Leave Room Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Do you want to leave #{roomCopy?.name}?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>You will no longer receive messages from this channel.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={leaveRoom}
              className="bg-destructive hover:bg-destructive/90"
            >
              <Trans>Leave</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Delete Room Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this channel</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Are you sure to delete this channel?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRoom}
              className="bg-destructive hover:bg-destructive/90"
            >
              <Trans>Delete now</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MyRoomOptionModal;

const PersonalRoomSettings: React.FC<{ roomId: string; onSave: () => void }> = ({ roomId, onSave }) => {
  const [settings, setSettings] = useState<RoomSetting>(new RoomSetting('placeholder'));
  const { toast } = useToast();
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    let sett = My.RoomSettings.get(roomId);
    if (!sett) {
      sett = new RoomSetting(roomId);
      if (My.user.notificationSettings?.find(n => n.roomId == roomId && n.option === 'disable-push-notification')) {
        sett.disablePushNotifications = true;
      }
    }
    setSettings(Util.pojo(sett));
  }, [roomId]);

  function toggleSetting(key: keyof RoomSetting) {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanged(true);
  }

  function saveChanges() {
    toast({
      title: t`Saved changes`,
      duration: 2000,
    });
    My.saveRoomNotificationSetting(roomId, settings);
    setHasChanged(false);
    onSave();
  }

  /* ... inside PersonalRoomSettings component ... */
  const settingItems = [
    {
      key: 'favoriteRoomInMyList' as keyof RoomSetting,
      label: <Trans>Add to favorites</Trans>,
      icon: Star,
      checked: settings.favoriteRoomInMyList,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
    },
    {
      key: 'hideRoomInMyList' as keyof RoomSetting,
      label: <Trans>Hide in My Channels list</Trans>,
      icon: EyeOff,
      checked: settings.hideRoomInMyList,
      color: "text-card-foreground",
      bgColor: "bg-muted",
    },
    {
      key: 'disablePushNotifications' as keyof RoomSetting,
      label: <Trans>Disable push notifications</Trans>,
      icon: BellOff,
      checked: settings.disablePushNotifications,
      color: "text-rose-600",
      bgColor: "bg-rose-100",
    },
    {
      key: 'allowMentionPushNotifications' as keyof RoomSetting,
      label: <Trans>Receive notifications when mentioned</Trans>,
      icon: Bell,
      checked: settings.allowMentionPushNotifications,
      color: "text-rose-600",
      bgColor: "bg-background",
      indent: true,
      hidden: !settings.disablePushNotifications
    },
    {
      key: 'disableVoiceCallPopups' as keyof RoomSetting,
      label: <Trans>Disable voice call popups</Trans>,
      icon: PhoneOff,
      checked: settings.disableVoiceCallPopups,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    {
      key: 'disableEmailReminders' as keyof RoomSetting,
      label: <Trans>Disable email reminders</Trans>,
      icon: MailX,
      checked: settings.disableEmailReminders,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      key: 'disableExternalNotifications' as keyof RoomSetting,
      label: <Trans>Disable external API notifications</Trans>,
      icon: BellOff,
      checked: settings.disableExternalNotifications,
      color: "text-indigo-600",
      bgColor: "bg-indigo-100",
    }
  ];

  return (
    <div className="space-y-4">
      <div className="border rounded-xl divide-y bg-background overflow-hidden shadow-sm">
        {settingItems.map((item: any) => {
          if (item.hidden) return null;
          const Icon = item.icon;
          const isIndented = item.indent;

          return (
            <div
              key={item.key}
              className={cn(
                "flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors cursor-pointer group",
                isIndented && "pl-12 bg-muted/50"
              )}
              onClick={() => toggleSetting(item.key)}
            >
              <div className={cn("p-2 rounded-lg flex-shrink-0 transition-colors", item.bgColor)}>
                <Icon className={cn("h-5 w-5", item.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <span className={cn(
                  "text-sm font-medium transition-colors",
                  item.checked ? "text-foreground" : "text-card-foreground"
                )}>
                  {item.label}
                </span>
              </div>
              <Checkbox
                id={item.key}
                checked={item.checked}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none"
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          onClick={saveChanges}
          disabled={!hasChanged}
          className="w-full sm:w-auto"
        >
          <Trans>Save changes</Trans>
        </Button>
      </div>
    </div>
  );
};